// complaint-map.component.ts
import { Component, OnInit, OnDestroy, signal, effect, inject, NgZone, AfterViewInit, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { ToolbarModule } from 'primeng/toolbar';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { ComplaintService, ComplaintModel } from '../../services/complaint.service';
import { ComplaintStatusService } from '../../services/complaint-status.service';
import { ComplaintTypeService } from '../../services/complaint-type.service';
import { GoogleMapsLoaderService } from '../../services/google-maps-loader.service';
import { GeolocationService, GeolocationResponse } from '../../services/geolocation.service';

/** Paleta de cores para os pins personalizados */
const PIN_COLORS = ['#f15a22', '#10949e', '#e67e22', '#2ecc71', '#e74c3c', '#3498db', '#9b59b6', '#1abc8c'];

@Component({
  selector: 'app-complaint-map',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CardModule,
    ButtonModule,
    SelectModule,
    TagModule,
    ToolbarModule,
    ToastModule,
  ],
  providers: [MessageService],
  template: `
    <p-card>
      <ng-template pTemplate="header">
        <p-toolbar>
          <ng-template pTemplate="start">
            <h2>Mapa de Denúncias</h2>
          </ng-template>
          <ng-template pTemplate="end">
            <p-button
              label="Recarregar"
              icon="pi pi-refresh"
              (onClick)="loadComplaints()"
              [outlined]="true"
              severity="secondary"
              size="small">
            </p-button>
          </ng-template>
        </p-toolbar>
      </ng-template>

      <ng-template pTemplate="content">
        <!-- Filtros -->
        <div class="filters-container p-3">
          <div class="filters-row">
            <p-select
              [(ngModel)]="selectedTypeId"
              [options]="typeOptions"
              (onChange)="applyFilters()"
              placeholder="Todos os Tipos"
              optionLabel="label"
              optionValue="value"
              [showClear]="true"
              class="w-full">
            </p-select>

            <p-select
              [(ngModel)]="selectedStatusId"
              [options]="statusOptions"
              (onChange)="applyFilters()"
              placeholder="Todos os Status"
              optionLabel="label"
              optionValue="value"
              [showClear]="true"
              class="w-full">
            </p-select>

            <span class="counter-badge">
              {{ filteredComplaints().length }} de {{ complaints().length }} denúncias
            </span>
          </div>
        </div>

        <!-- Container do mapa com ViewChild -->
        <div #mapContainer id="complaintMap" class="map-container"></div>

        <!-- Overlay de loading -->
        @if (isLoading() || !mapReady()) {
          <div class="loading-overlay">
            <i class="pi pi-spin pi-spinner" style="font-size: 2rem; color: #10949e;"></i>
            <span>{{ isLoading() ? 'Carregando denúncias...' : 'Carregando mapa...' }}</span>
          </div>
        }

        <!-- Mensagem quando não há coordenadas -->
        @if (!isLoading() && mapReady() && filteredComplaints().length > 0 && totalWithCoords() === 0) {
          <div class="info-message">
            <i class="pi pi-info-circle"></i>
            Nenhuma denúncia possui coordenadas geográficas para exibir no mapa.
          </div>
        }
      </ng-template>
    </p-card>

    <p-toast></p-toast>
  `,
  styles: [`
    .filters-container {
      border-bottom: 1px solid #e0e0e0;
    }

    .filters-row {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      flex-wrap: wrap;
    }

    .filters-row p-select {
      min-width: 220px;
      max-width: 300px;
    }

    .counter-badge {
      font-size: 0.85rem;
      color: #6c757d;
      background: #f8f9fa;
      padding: 0.4rem 0.8rem;
      border-radius: 20px;
      border: 1px solid #dee2e6;
      white-space: nowrap;
    }

    .map-container {
      height: calc(100vh - 310px);
      min-height: 450px;
      width: 100%;
      border-radius: 6px;
      margin-top: 0.5rem;
      border: 1px solid #e0e0e0;
      position: relative;
      background: #f8f9fa;
    }

    .loading-overlay {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 0.75rem;
      padding: 4rem 0;
      color: #6c757d;
    }

    .info-message {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      padding: 3rem 0;
      color: #6c757d;
      font-size: 1rem;
    }

    :host ::ng-deep .p-toolbar {
      border: none;
      padding: 1rem 0;
    }

    :host ::ng-deep .p-card .p-card-content {
      padding: 0;
    }
     #complaintMap {
    height: 100%;
    width: 100%;
  }
  `]
})
export class ComplaintMapComponent implements OnInit, OnDestroy, AfterViewInit {
  // ViewChild para referência ao elemento do mapa
  @ViewChild('mapContainer') mapContainerRef!: ElementRef;

  // Signals
  complaints = signal<ComplaintModel[]>([]);
  filteredComplaints = signal<ComplaintModel[]>([]);
  isLoading = signal(false);
  mapReady = signal(false);

  /** Total de denúncias filtradas que possuem coordenadas válidas */
  totalWithCoords = signal(0);

  // Filtros
  selectedTypeId: number | null = null;
  selectedStatusId: number | null = null;

  // Opções dos selects
  typeOptions: { label: string; value: number }[] = [];
  statusOptions: { label: string; value: number }[] = [];

  // Google Maps
  private map: google.maps.Map | null = null;
  private markers: google.maps.Marker[] = [];
  private infoWindow: google.maps.InfoWindow | null = null;
  private mapInitialized = false;
  private apiLoaded = signal(false);
  private initRetryCount = 0;
  private maxRetries = 10; // Aumentado para dar mais chances
  private mapElement: HTMLElement | null = null;

  constructor(
    private complaintService: ComplaintService,
    private complaintStatusService: ComplaintStatusService,
    private complaintTypeService: ComplaintTypeService,
    private geolocationService: GeolocationService,
    private googleMapsLoader: GoogleMapsLoaderService,
    private router: Router,
    private messageService: MessageService,
    private ngZone: NgZone
  ) {
    // Efeito para inicialização do mapa
    effect(() => {
      const data = this.filteredComplaints();
      const isLoading = this.isLoading();
      const apiLoaded = this.apiLoaded();
      
      if (!isLoading && apiLoaded && !this.mapInitialized && data.length > 0) {
        console.log('Efeito: Tentando inicializar mapa...');
        // Aguarda o DOM estar pronto
        this.scheduleMapInitialization(100);
      }
    });
  }

  ngOnInit(): void {
    console.log('Componente inicializado');
    this.googleMapsLoader.load()
      .then(() => {
        console.log('Google Maps API carregada com sucesso');
        this.apiLoaded.set(true);
        this.mapReady.set(true);
        this.loadFiltersAndData();
      })
      .catch((err) => {
        console.error('Erro ao carregar Google Maps API:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Não foi possível carregar o Google Maps. Verifique sua chave de API.'
        });
        this.isLoading.set(false);
        this.mapReady.set(true);
      });
  }

  ngAfterViewInit(): void {
    // Aguarda o ViewChild estar disponível
    setTimeout(() => {
      if (this.mapContainerRef) {
        this.mapElement = this.mapContainerRef.nativeElement;
        console.log('ViewChild do mapa disponível');
        // Se a API já estiver carregada, tenta inicializar
        if (this.apiLoaded()) {
          this.tryInitializeMap();
        }
      }
    }, 100);
  }

  ngOnDestroy(): void {
    console.log('Componente destruído - limpando recursos');
    this.clearMarkers();
    if (this.map) {
      this.map = null;
    }
    this.infoWindow = null;
    this.mapInitialized = false;
    window.removeEventListener('resize', this.onResize);
  }

  // ===================== CARGA DE DADOS =====================

  private scheduleMapInitialization(delay: number): void {
    setTimeout(() => {
      this.ngZone.runOutsideAngular(() => {
        this.tryInitializeMap();
      });
    }, delay);
  }

  private tryInitializeMap(): void {
    // Usa o ViewChild ou fallback para getElementById
    let el = this.mapElement || document.getElementById('complaintMap');
    
    if (!el) {
      console.warn('Elemento do mapa não encontrado');
      this.retryInitialization('Elemento não encontrado');
      return;
    }

    // Verifica se o elemento está visível e tem dimensões
    const rect = el.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      console.warn('Mapa não está visível, dimensões:', rect);
      this.retryInitialization('Dimensões zero');
      return;
    }

    console.log('Elemento do mapa encontrado e visível:', { 
      width: rect.width, 
      height: rect.height,
      innerWidth: window.innerWidth,
      innerHeight: window.innerHeight
    });

    // Limpa o container para evitar conflitos
    el.innerHTML = '';
    this.initMap(el);
  }

  private retryInitialization(reason: string): void {
    if (this.initRetryCount < this.maxRetries) {
      this.initRetryCount++;
      const delay = 500 * this.initRetryCount;
      console.log(`Tentativa ${this.initRetryCount}/${this.maxRetries} de inicializar o mapa. Razão: ${reason}. Aguardando ${delay}ms`);
      
      setTimeout(() => {
        this.ngZone.runOutsideAngular(() => {
          this.tryInitializeMap();
        });
      }, delay);
    } else {
      console.error('Número máximo de tentativas atingido para inicializar o mapa');
      this.messageService.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Não foi possível inicializar o mapa após várias tentativas.'
      });
    }
  }

  private loadFiltersAndData(): void {
    this.isLoading.set(true);
    
    this.complaintStatusService.findAll().subscribe({
      next: (statuses) => {
        this.statusOptions = statuses.map(s => ({
          label: s.description ?? '',
          value: s.id!,
        }));

        const pending = statuses.find(
          s => s.description?.toLowerCase() === 'aguardando análise',
        );
        this.selectedStatusId = pending?.id ?? null;

        this.complaintTypeService.findAll().subscribe({
          next: (types) => {
            this.typeOptions = types.map(t => ({
              label: t.description ?? '',
              value: t.id!,
            }));

            const abandoned = types.find(
              t => t.description?.toLowerCase() === 'veiculos abandonados',
            );
            this.selectedTypeId = abandoned?.id ?? null;

            this.loadComplaints();
          },
          error: (err) => {
            console.error('Erro ao carregar tipos:', err);
            this.loadComplaints();
          },
        });
      },
      error: (err) => {
        console.error('Erro ao carregar status:', err);
        this.loadComplaints();
      },
    });
  }

  loadComplaints(): void {
    this.isLoading.set(true);
    this.clearMarkers();

    this.complaintService.findAllFiltered(
      undefined,
      undefined,
      this.selectedTypeId ?? undefined,
      this.selectedStatusId ?? undefined,
    ).subscribe({
      next: (data) => {
        console.log('Denúncias carregadas:', data.length);
        this.complaints.set(data);
        this.filteredComplaints.set(data);
        this.isLoading.set(false);
        
        if (this.mapInitialized && this.map) {
          console.log('Atualizando marcadores no mapa existente');
          this.addMarkersToMap();
        } else if (data.length > 0) {
          console.log('Tentando inicializar mapa após carregar dados');
          this.scheduleMapInitialization(300);
        }
      },
      error: (err) => {
        console.error('Erro ao carregar denúncias:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Não foi possível carregar as denúncias.'
        });
        this.isLoading.set(false);
        this.mapReady.set(true);
      }
    });
  }

  // ===================== MAPA (Google Maps) =====================

  private initMap(mapElement: HTMLElement): void {
    if (this.mapInitialized || !this.apiLoaded()) {
      console.log('Mapa já inicializado ou API não carregada', {
        mapInitialized: this.mapInitialized,
        apiLoaded: this.apiLoaded()
      });
      return;
    }

    if (!mapElement) {
      console.error('Elemento do mapa não fornecido');
      return;
    }

    try {
      // Certifica-se de que o elemento tem um ID
      if (!mapElement.id) {
        mapElement.id = 'complaintMap';
      }

      console.log('Inicializando mapa com elemento:', mapElement.id);

      const fortaleza = new google.maps.LatLng(-3.7319, -38.5267);
      const mapOptions: google.maps.MapOptions = {
        center: fortaleza,
        zoom: 12,
        mapTypeId: google.maps.MapTypeId.ROADMAP,
        mapTypeControl: false,
        streetViewControl: true,
        fullscreenControl: true,
        zoomControl: true,
      };

      // Cria o mapa
      this.map = new google.maps.Map(mapElement, mapOptions);
      this.infoWindow = new google.maps.InfoWindow();
      this.mapInitialized = true;
      
      console.log('Mapa inicializado com sucesso');

      // Listener para resize da janela
      window.addEventListener('resize', this.onResize);

      // Força o redimensionamento
      setTimeout(() => {
        if (this.map) {
          google.maps.event.trigger(this.map, 'resize');
          console.log('Mapa redimensionado');
        }
      }, 300);

      // Adiciona marcadores
      this.addMarkersToMap();

    } catch (err) {
      console.error('Falha ao criar mapa:', err);
      this.mapInitialized = false;
      this.map = null;
      this.retryInitialization('Erro ao criar mapa: ' + (err instanceof Error ? err.message : 'Erro desconhecido'));
    }
  }

  private onResize = (): void => {
    if (this.map) {
      google.maps.event.trigger(this.map, 'resize');
    }
  };

  private clearMarkers(): void {
    this.markers.forEach(marker => {
      try {
        marker.setMap(null);
      } catch (e) {
        console.warn('Erro ao limpar marcador:', e);
      }
    });
    this.markers = [];
  }

  private addMarkersToMap(): void {
    if (!this.map || !this.infoWindow) {
      console.warn('Mapa ou InfoWindow não disponível para adicionar marcadores');
      return;
    }

    this.clearMarkers();

    const withCoords: ComplaintModel[] = [];
    const needsGeocoding: ComplaintModel[] = [];

    for (const c of this.filteredComplaints()) {
      const hasCoords = c.latitude && c.longitude &&
        c.latitude !== '0' && c.longitude !== '0' &&
        c.latitude !== '' && c.longitude !== '';

      if (hasCoords) {
        withCoords.push(c);
      } else if (c.address && c.address.trim() !== '') {
        needsGeocoding.push(c);
      }
    }

    console.log(`Adicionando marcadores: ${withCoords.length} com coordenadas, ${needsGeocoding.length} para geocodificar`);

    const bounds = new google.maps.LatLngBounds();
    let markerCount = 0;

    withCoords.forEach((complaint, index) => {
      const lat = parseFloat(complaint.latitude!);
      const lng = parseFloat(complaint.longitude!);
      if (isNaN(lat) || isNaN(lng)) {
        console.warn(`Coordenadas inválidas para denúncia ${complaint.id}:`, complaint.latitude, complaint.longitude);
        return;
      }

      const position = new google.maps.LatLng(lat, lng);
      bounds.extend(position);
      this.createMarker(complaint, position, PIN_COLORS[index % PIN_COLORS.length]);
      markerCount++;
    });

    if (needsGeocoding.length > 0) {
      this.processGeocoding(needsGeocoding, bounds, markerCount);
    } else {
      this.finalizeMarkers(bounds, withCoords.length + needsGeocoding.length);
    }
  }

  private processGeocoding(
    complaints: ComplaintModel[],
    bounds: google.maps.LatLngBounds,
    startIndex: number
  ): void {
    let completed = 0;
    let geocodedCount = 0;
    const total = complaints.length;

    console.log(`Iniciando geocodificação de ${total} endereços`);

    complaints.forEach((complaint) => {
      const address = complaint.address!.trim();

      this.geolocationService.getCoordinatesByAddress(address).subscribe({
        next: (res: GeolocationResponse) => {
          completed++;
          if (res.success && res.latitude != null && res.longitude != null) {
            const position = new google.maps.LatLng(res.latitude, res.longitude);
            bounds.extend(position);
            this.createMarker(complaint, position, '#3498db', res.formattedAddress);
            geocodedCount++;
            console.log(`Endereço geocodificado: ${address} -> ${res.latitude}, ${res.longitude}`);
          } else {
            console.warn(`Falha ao geocodificar: ${address} - ${res.errorMessage || 'Erro desconhecido'}`);
          }
          if (completed === total) {
            this.finalizeMarkers(bounds, startIndex + geocodedCount);
          }
        },
        error: (err) => {
          completed++;
          console.error(`Erro ao geocodificar ${address}:`, err);
          if (completed === total) {
            this.finalizeMarkers(bounds, startIndex + geocodedCount);
          }
        }
      });
    });
  }

  private createMarker(
    complaint: ComplaintModel,
    position: google.maps.LatLng,
    color: string,
    geocodedAddress?: string
  ): void {
    if (!this.map) return;

    try {
      const marker = new google.maps.Marker({
        position,
        map: this.map,
        title: `Denúncia #${complaint.id}`,
        icon: this.createPinIcon(color),
        animation: google.maps.Animation.DROP,
      });

      const typeDesc = complaint.complaintType?.description ?? 'Sem tipo';
      const statusDesc = complaint.complaintStatus?.description ?? 'Sem status';
      const address = complaint.address ?? 'Endereço não informado';
      const geocodeNote = geocodedAddress
        ? `<p style="margin: 3px 0; font-size: 0.8rem; color: #3498db;"><strong>Geolocalizado:</strong> ${this.escapeHtml(geocodedAddress)}</p>`
        : '';

      marker.addListener('click', () => {
        if (this.infoWindow) {
          this.infoWindow.setContent(`
            <div style="font-family: 'Segoe UI', Roboto, sans-serif; padding: 4px; min-width: 220px;">
              <h3 style="margin: 0 0 6px 0; font-size: 1rem; color: #f15a22;">
                Denúncia #${complaint.id}
              </h3>
              <p style="margin: 3px 0; font-size: 0.85rem; color: #333;">
                <strong>Tipo:</strong> ${this.escapeHtml(typeDesc)}
              </p>
              <p style="margin: 3px 0; font-size: 0.85rem; color: #333;">
                <strong>Status:</strong> ${this.escapeHtml(statusDesc)}
              </p>
              <p style="margin: 3px 0; font-size: 0.85rem; color: #333;">
                <strong>Endereço:</strong> ${this.escapeHtml(address)}
              </p>
              ${geocodeNote}
              <div style="margin-top: 8px;">
                <a href="/home/complaints/details/${complaint.id}"
                   style="color: #10949e; font-weight: 500; text-decoration: none;">
                  Ver detalhes →
                </a>
              </div>
            </div>
          `);
          this.infoWindow.open(this.map, marker);
        }
      });

      this.markers.push(marker);
    } catch (err) {
      console.error('Erro ao criar marcador:', err);
    }
  }

  private finalizeMarkers(bounds: google.maps.LatLngBounds, total: number): void {
    this.totalWithCoords.set(total);
    
    if (this.markers.length > 0 && this.map) {
      console.log(`Ajustando mapa com ${this.markers.length} marcadores`);
      this.map.fitBounds(bounds, 60);
    } else if (this.map && total === 0) {
      console.log('Nenhum marcador para exibir, centralizando em Fortaleza');
      this.map.setCenter(new google.maps.LatLng(-3.7319, -38.5267));
      this.map.setZoom(12);
    }
  }

  private createPinIcon(color: string): google.maps.Icon {
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 36" width="24" height="36">
        <path d="M12 0C7 0 3 4 3 9c0 7 9 18 9 18s9-11 9-18c0-5-4-9-9-9z"
              fill="${color}" stroke="#333" stroke-width="0.5"/>
        <circle cx="12" cy="9" r="4" fill="white"/>
      </svg>
    `;
    return {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
      scaledSize: new google.maps.Size(24, 36),
      anchor: new google.maps.Point(12, 36),
      labelOrigin: new google.maps.Point(12, 8),
    };
  }

  // ===================== FILTROS =====================

  applyFilters(): void {
    console.log('Aplicando filtros:', { 
      typeId: this.selectedTypeId, 
      statusId: this.selectedStatusId 
    });
    this.loadComplaints();
  }

  // ===================== UTILITÁRIOS =====================

  private escapeHtml(text: string): string {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}